"use client";

import { useState } from "react";
import {
  Code,
  FileText,
  Globe,
  Search,
  Mail,
  Brain,
  ChevronLeft,
  ChevronRight,
  Sparkles,
} from "lucide-react";

const WhatYoullLearn = () => {
  const [scrollPosition, setScrollPosition] = useState(0);

  const learningItems = [
    {
      icon: <Code className="w-6 h-6 text-blue-500" />,
      title: "DSA Strategy",
      subtitle: "👨‍💻 A DSA strategy that doesn't feel like a full-time job",
      description: "Smart preparation, not endless grinding",
      codeSnippet: "optimize(preparation)",
    },
    {
      icon: <FileText className="w-6 h-6 text-indigo-500" />,
      title: "Resume Tricks",
      subtitle: "🧾 Resume tricks that actually work for tech roles",
      description: "Stand out from the template crowd",
      codeSnippet: "resume.standOut()",
    },
    {
      icon: <Globe className="w-6 h-6 text-green-500" />,
      title: "Online Presence",
      subtitle: "🌐 GitHub, LinkedIn & Portfolio that look good and convert",
      description: "Professional presence that gets noticed",
      codeSnippet: "profile.convert()",
    },
    {
      icon: <Search className="w-6 h-6 text-purple-500" />,
      title: "Hidden Opportunities",
      subtitle: "🕵‍♂ How to find SDE internships (not just on Internshala)",
      description: "Hidden opportunities everywhere",
      codeSnippet: "findOpportunities()",
    },
    {
      icon: <Mail className="w-6 h-6 text-amber-500" />,
      title: "Cold Outreach",
      subtitle: "📩 Cold mail templates that don't get ignored",
      description: "Professional outreach that works",
      codeSnippet: "sendEmail.success()",
    },
    {
      icon: <Brain className="w-6 h-6 text-pink-500" />,
      title: "Interview Mastery",
      subtitle: "🧠 Mock OAs, tech rounds, HR rounds—crack them all",
      description: "Complete interview preparation",
      codeSnippet: "crackInterview()",
    },
  ];

  const scrollLeft = () => {
    const container = document.getElementById("learning-container");
    if (container) {
      container.scrollBy({ left: -280, behavior: "smooth" });
      setScrollPosition(container.scrollLeft - 280);
    }
  };

  const scrollRight = () => {
    const container = document.getElementById("learning-container");
    if (container) {
      container.scrollBy({ left: 280, behavior: "smooth" });
      setScrollPosition(container.scrollLeft + 280);
    }
  };

  return (
    <div className="bg-black py-32 lg:py-16 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/30 backdrop-blur-sm">
            <div className="w-4 h-4 bg-blue-400 rounded-full animate-pulse" />
            <span className="text-sm font-semibold text-blue-400 tracking-wide uppercase">
              What You'll Learn
            </span>
          </div>
        </div>

        <div className="space-y-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-400">
              What will you really learn?
            </h2>
          </div>

          {/* Desktop Grid View */}
          <div className="hidden md:grid md:grid-cols-2 md:gap-6">
            {learningItems.map((item, index) => (
              <div
                key={index}
                className="group relative bg-black border border-blue-500/30 rounded-lg p-6 hover:border-blue-400 transition-all duration-300 hover:bg-gradient-to-br hover:from-blue-900/20 hover:to-indigo-900/20"
              >
                <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg blur opacity-0 group-hover:opacity-30 transition duration-300" />

                <div className="relative">
                  {/* Header with icon and code */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 rounded-lg bg-blue-500/10 shrink-0">
                        {item.icon}
                      </div>
                      <h3 className="text-lg font-bold text-white group-hover:text-blue-300 transition-colors">
                        {item.title}
                      </h3>
                    </div>
                    <div className="px-2 py-1 bg-gray-800/50 rounded text-xs font-mono text-gray-400 border border-gray-700">
                      {item.codeSnippet}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="space-y-2">
                    <p className="text-white md:text-sm font-medium md:whitespace-nowrap">
                      {item.subtitle}
                    </p>
                    <p className="text-gray-400 text-sm group-hover:text-gray-200 transition-colors">
                      {item.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile Cards - Centered layout */}
          <div className="md:hidden space-y-4">
            {learningItems.map((item, index) => (
              <div
                key={index}
                className="group relative bg-black border border-blue-500/30 rounded-lg p-6 hover:border-blue-400 transition-all duration-300 hover:bg-gradient-to-br hover:from-blue-900/20 hover:to-indigo-900/20"
              >
                <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg blur opacity-0 group-hover:opacity-30 transition duration-300" />

                <div className="relative">
                  {/* Mobile: Centered icon and title */}
                  <div className="flex flex-col items-center text-center mb-4">
                    <div className="p-3 rounded-lg bg-blue-500/10 mb-3">
                      {item.icon}
                    </div>
                    <h3 className="text-lg font-bold text-white group-hover:text-blue-300 transition-colors mb-2">
                      {item.title}
                    </h3>
                    <div className="px-3 py-1 bg-gray-800/50 rounded text-xs font-mono text-gray-400 border border-gray-700">
                      {item.codeSnippet}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="space-y-2 text-center">
                    <p className="text-white font-medium">{item.subtitle}</p>
                    <p className="text-gray-400 text-sm group-hover:text-gray-200 transition-colors">
                      {item.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Scroll Controls for Mobile */}
          {/* <div className="md:hidden flex justify-center gap-4 mb-4">
            <button
              onClick={scrollLeft}
              className="p-2 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-400"
              aria-label="Scroll left"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={scrollRight}
              className="p-2 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-400"
              aria-label="Scroll right"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div> */}

          {/* Bottom Summary */}
          <div className="mt-12 text-center">
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-black to-blue-950 border border-blue-500/20 p-6 md:p-8">
              <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl -mr-32 -mt-32" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl -ml-32 -mb-32" />

              <div className="relative z-10 flex items-center justify-center gap-3">
                <Sparkles className="w-6 h-6 text-amber-400 animate-pulse" />
                <p className="text-xl md:text-xl font-medium text-blue-100 md:whitespace-nowrap">
                  …Basically, the things no one teaches in college, but every
                  intern needs.
                </p>
                <Sparkles className="w-6 h-6 text-amber-400 animate-pulse" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatYoullLearn;
